import { Article } from '../../components/Layout'
import { H1, H2, H3, P, Lead, UL, LI, Callout, Steps, Step, Code, Tag, Screenshot, Grid, Card } from '../../components/DocComponents'

const toc = [
  { id: 'what', title: 'What is a Plate?', level: 2 as const },
  { id: 'lifecycle', title: 'Lifecycle', level: 2 as const },
  { id: 'create', title: 'Creating a Plate', level: 2 as const },
  { id: 'structure', title: 'Structure', level: 2 as const },
  { id: 'running', title: 'Running a Plate', level: 2 as const },
]

export function PlatesOverview() {
  return (
    <Article toc={toc}>
      <H1>What are Plates?</H1>
      <Lead>A Plate is a configurable dashboard page. It displays live data, embeds interactive forms, and lets users navigate between related views — all without writing any frontend code.</Lead>

      <H2 id="what">What is a Plate?</H2>
      <P>A Plate is Rabbit's answer to "how do I show data to users without building a custom frontend?". You configure it visually in the Console and deploy it. The Plate Runner then renders it as a fully interactive page.</P>
      <Grid cols={2}>
        <Card title="No-code dashboard" icon="📊">Compose views for tables, lists, charts, cards, and buttons. No React, no HTML — just configuration.</Card>
        <Card title="Live data" icon="🔄">Every view fetches data in real time from a Drinker script or directly from a Cup via the Coffee API.</Card>
        <Card title="Embedded workflows" icon="⚙️">Preparation Views embed an Interviewer directly in the dashboard — users can fill in data without leaving the plate.</Card>
        <Card title="Public access" icon="🌍">Publish a plate to OpenPlace for anonymous access — no login required. Ideal for customer-facing forms.</Card>
      </Grid>

      <H2 id="lifecycle">Lifecycle</H2>
      <P>A Plate moves through three states:</P>
      <div className="flex items-center gap-3 mb-5 flex-wrap">
        <div className="flex items-center gap-2"><Tag color="slate">draft</Tag><span className="text-sm text-slate-500">Being configured — cannot be opened by users</span></div>
        <span className="text-slate-300">→</span>
        <div className="flex items-center gap-2"><Tag color="blue">verified</Tag><span className="text-sm text-slate-500">All views are valid — ready to deploy</span></div>
        <span className="text-slate-300">→</span>
        <div className="flex items-center gap-2"><Tag color="green">deployed</Tag><span className="text-sm text-slate-500">Live and accessible via the Plate Runner</span></div>
      </div>
      <Callout type="info">Every time you edit a view or section, the plate reverts to <Tag color="slate">draft</Tag>. You must verify and re-deploy before the changes are visible to users.</Callout>

      <H2 id="create">Creating a Plate</H2>
      <Steps>
        <Step n={1} title="Open Plates">Go to <strong>Brainer › Plates</strong> and click <strong>New Plate</strong>.</Step>
        <Step n={2} title="Name it">Give the plate a descriptive name — this is shown in the Plate Runner tab title.</Step>
        <Step n={3} title="Add sections and views">Use the Builder tab to add Sections (layout columns) and Views (data widgets) inside each section.</Step>
        <Step n={4} title="Add input parameters">Use the Parameters tab to define the data the plate needs (e.g. a cup ID, a customer ID).</Step>
        <Step n={5} title="Verify">Click <strong>Verify</strong>. Rabbit checks all views are correctly configured.</Step>
        <Step n={6} title="Deploy">Click <strong>Deploy</strong>. The plate is now live.</Step>
      </Steps>
      <Screenshot alt="Plate detail page showing sections on the left, a Builder tab, Parameters tab, and Verify/Deploy buttons" caption="The plate detail page is the central hub for configuring your dashboard." />

      <H2 id="structure">Structure</H2>
      <P>A Plate is composed of:</P>
      <UL>
        <LI><strong>Sections</strong> — Horizontal layout columns, each with a percentage width. See <a href="/docs/plates/sections" className="text-blue-600 underline underline-offset-2">Sections &amp; Layout</a>.</LI>
        <LI><strong>Views</strong> — Data widgets inside sections. See <a href="/docs/plates/views" className="text-blue-600 underline underline-offset-2">View Types</a>.</LI>
        <LI><strong>Input Parameters</strong> — Named values injected into all views at runtime. See <a href="/docs/plates/input-params" className="text-blue-600 underline underline-offset-2">Input Parameters</a>.</LI>
      </UL>

      <H2 id="running">Running a Plate</H2>
      <P>Once deployed, a plate is accessible in two ways:</P>
      <UL>
        <LI><strong>Plate Runner</strong> — Click the ▶ Run button on the plate card in the Console. Opens the plate in an authenticated runner at <Code>/run/:plateId</Code>.</LI>
        <LI><strong>OpenPlace</strong> — If published, the plate is accessible without authentication at <Code>/open/:plateId</Code>. See <a href="/docs/plates/openplace" className="text-blue-600 underline underline-offset-2">OpenPlace</a>.</LI>
      </UL>
      <Callout type="tip">To link from one plate to another, use a <strong>Button View</strong> or a <strong>Section Button</strong>. Query params in the URL are automatically forwarded as input parameters to the destination plate.</Callout>
    </Article>
  )
}
